import { FC } from "react";
import { useTranslation } from "react-i18next";
import RangeSlider from "../../components/range-slider";
import { STEP_LABELS_OPTIONS, STEP_VALUES } from "../../constants";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldInput,
} from "../../../../components/admin/formFields";
import useConversion from "../../../../hooks/conversion";
import { REPORT_FIELD_OUTPUT_TYPES } from "../../../reports/constants";
import {
  DISTANCE_UNITS,
  SPEED_UNITS,
} from "../../../admin-details/account/constants";
import { convertKmphTo, convertSpeedToKmph } from "../../../../utils/distance";

interface HarshDrivingProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  setFieldTouched: (field: string, isTouched: boolean) => void;
}

const HarshDriving: FC<HarshDrivingProps> = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handleBlur,
  setFieldTouched,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.sensor_event_detection",
  });
  const { getUnitByOutputType } = useConversion();

  const getSpeedUnit = () => {
    return getUnitByOutputType(REPORT_FIELD_OUTPUT_TYPES.KPH_TO_SPEED_UNITS);
  };

  const EVENT_TRIGGER_HARSH = [
    {
      id: "harsh_acceleration_sensitivity",
      value: values.harsh_acceleration_sensitivity,
      label: "harsh_acceleration",
      speed_id: "harsh_acceleration_speed",
      speed_value: values.harsh_acceleration_speed,
      speed_label: "speed",
      speed_error: errors.harsh_acceleration_speed,
      speed_touched: touched.harsh_acceleration_speed,
    },
    {
      id: "harsh_braking_sensitivity",
      value: values.harsh_braking_sensitivity,
      label: "harsh_braking",
      speed_id: "harsh_braking_speed",
      speed_value: values.harsh_braking_speed,
      speed_label: "speed_discrepancy",
      speed_error: errors.harsh_braking_speed,
      speed_touched: touched.harsh_braking_speed,
    },
    {
      id: "harsh_cornering_sensitivity",
      value: values.harsh_cornering_sensitivity,
      label: "harsh_cornering",
      speed_id: "harsh_cornering_speed",
      speed_value: values.harsh_cornering_speed,
      speed_label: "speed",
      speed_error: errors.harsh_cornering_speed,
      speed_touched: touched.harsh_cornering_speed,
    },
  ];
  return (
    <>
      <div className="col-span-6 p-6 border-b space-y-1">
        <div className="w-full space-y-2 mb-4">
          <h1 className="text-lg text-accent-blue-dark font-bold mb-4">
            {t("harsh_driving")}
          </h1>
          <h2 className="block font-semibold text-sm leading-none tracking-normal">
            {t("event_trigger")}
          </h2>
          <p className="font-normal text-sm leading-none mb-4 text-gray-500">
            {t("event_trigger_description")}
          </p>
        </div>
        <div className="w-full grid grid-cols-6 items-center pb-2 font-medium text-sm leading-none">
          <div className="col-span-4 flex gap-2">
            <div className="w-1/3">{t("event_type")}</div>
            <div className="w-full flex justify-between">
              <div>{t("low_less_events")}</div>
              <div>{t("sensitivity")}</div>
              <div>{t("high_less_events")}</div>
            </div>
          </div>
          <div className="col-span-2 ml-16">
            {t("triggered_thresold")}{" "}
            {getSpeedUnit() === SPEED_UNITS.MPH
              ? t("mph")
              : getSpeedUnit() === SPEED_UNITS.NAUTICAL_MILES
                ? t("nm")
                : t("kmh")}
          </div>
        </div>
        {EVENT_TRIGGER_HARSH?.map((harsh, index) => (
          <div
            className="w-full grid grid-cols-6 space-x-16 items-center"
            key={index}
          >
            <div className="col-span-4">
              <RangeSlider
                id={harsh.id}
                min={STEP_VALUES.min}
                max={STEP_VALUES.max}
                step={STEP_VALUES.step}
                value={harsh.value}
                onChange={handleChange}
                stepLabels={STEP_LABELS_OPTIONS}
                isTopLabel={false}
                isLeftLabel={true}
                leftLabel={t(harsh.label)}
              />
            </div>
            <AdminFormFieldInput
              type="number"
              label={`${t(harsh.speed_label)} ${
                getSpeedUnit() === SPEED_UNITS.MPH
                  ? t("mph")
                  : getSpeedUnit() === SPEED_UNITS.NAUTICAL_MILES
                    ? t("nm")
                    : t("kmh")
              }`}
              name={harsh.speed_id}
              id={harsh.speed_id}
              value={harsh.speed_value}
              // onChange={(e) => {
              //   let result = convertSpeedToKmph(
              //     getSpeedUnit() as SPEED_UNITS,
              //     Number(e.target.value)
              //   );
              //   setFieldValue(harsh.speed_id, Number(result.toFixed(2)));
              // }}
              onChange={handleChange}
              onBlur={handleBlur}
              error={harsh.speed_error}
              touched={harsh.speed_value}
              customWrapperClass="col-span-2"
            />
          </div>
        ))}
      </div>
      <div className="col-span-6 p-6 border-b space-y-1">
        <div className="w-full space-y-2 mb-4">
          <h2 className="block font-semibold text-sm leading-none tracking-normal">
            {t("event_recording")}
          </h2>
          <p className="font-normal text-sm leading-none mb-4 text-gray-500">
            {t("event_recording_description")}
          </p>
        </div>
        <div className="col-span-6 grid grid-cols-12 gap-6">
          <AdminFormFieldInput
            type="number"
            label={t("pre_event_video_duration_sec")}
            name="harsh_pre_event_video_duration_sec"
            id="harsh_pre_event_video_duration_sec"
            value={values.harsh_pre_event_video_duration_sec}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.harsh_pre_event_video_duration_sec}
            touched={touched.harsh_pre_event_video_duration_sec}
            customWrapperClass="col-span-6"
          />
          <AdminFormFieldInput
            type="number"
            label={t("post_event_video_duration_sec")}
            name="harsh_post_event_video_duration_sec"
            id="harsh_post_event_video_duration_sec"
            value={values.harsh_post_event_video_duration_sec}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.harsh_post_event_video_duration_sec}
            touched={touched.harsh_post_event_video_duration_sec}
            customWrapperClass="col-span-6"
          />
        </div>
      </div>
      <div className="col-span-6 p-6 space-y-1">
        <h2 className="block font-semibold text-sm leading-none tracking-normal mb-4">
          {t("alerts")}
        </h2>
        <AdminFormFieldCheckbox
          customWrapperClass="w-1/2"
          label={t("in_cabin_audio_alert")}
          id="harsh_in_cabin_audio_alert"
          type="checkbox"
          name="harsh_in_cabin_audio_alert"
          checked={values.harsh_in_cabin_audio_alert}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.harsh_in_cabin_audio_alert}
          touched={touched.harsh_in_cabin_audio_alert}
        />
      </div>
    </>
  );
};

export default HarshDriving;
